import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Label } from "reactstrap";
import Select from "react-select";
import _ from "lodash";
import Swal from "sweetalert2";
import FieldWrapper from "../commons/FieldWrapper";
import { getAllCourses, getAllApplicants, movePostulationsToCourse } from "./Axios";

const str = v => String(v ?? "");

const normTxt = (t) =>
  str(t).trim().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

const MoveToCourseModal = ({ courseId, _applicantsSelected = [], reloadQuery }) => {
  const [modal, setModal] = useState(false);

  const [courses, setCourses] = useState([]);
  const [targetCourse, setTargetCourse] = useState(null);
  const [applicantOptions, setApplicantOptions] = useState([]);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [errors, setErrors] = useState({});

  const toggle = () => setModal(m => !m);

  const isSelected = (opt) => {
    const byId = selectedApplicants.some(s => str(s.value) === str(opt.value));
    if (byId) return true;
    return selectedApplicants.some(s => normTxt(s.label) === normTxt(opt.label));
  };

  const availableOptions = useMemo(
    () => applicantOptions.filter(o => !isSelected(o)),
    [applicantOptions, selectedApplicants]
  );

  useEffect(() => {
    if (!modal) return;

    getAllCourses(resp => {
      const list = (resp?.data || [])
        .filter(c => (courseId ? String(c.id) !== String(courseId) : true))
        .map(c => ({ value: c.id, label: `${c.title} — ID: ${c.id}` }));
      setCourses(list);
    });

    getAllApplicants(courseId, resp => {
      const opts = (resp?.data || []).map(a => ({
        value: str(a.id),
        label: a.full_name,
        kind:  "applicant"
      }));
      setApplicantOptions(opts);
    });

    const defaultKind = courseId ? "postulation" : "applicant";
    const pre = (_applicantsSelected || []).map(a => ({
      value: str(a.value),
      label: a.label,
      kind:  a.kind || defaultKind
    }));
    setSelectedApplicants(_.uniqBy(pre, (x) => `${x.kind}:${x.value}`));
  }, [modal, courseId, _applicantsSelected]);

  const validate = () => {
    const e = {};
    if (!targetCourse) e.target = "Debe seleccionar el proceso destino.";
    if (!selectedApplicants || selectedApplicants.length === 0) {
      e.applicants = "Debe seleccionar al menos un postulante.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = () => {
    if (!validate()) return;

    Swal.showLoading();

    const encodedIds = selectedApplicants.map(item => {
      if (courseId) {
        const kind = item.kind === "postulation" ? "p" : "a";
        return `${kind}:${item.value}`;
      }
      return `a:${item.value}`;
    });

    const sourceCourseIdForUrl = courseId || targetCourse.value;

    movePostulationsToCourse(sourceCourseIdForUrl, targetCourse.value, encodedIds, (resp) => {
      Swal.close();

      if (!resp || resp.status !== 200) {
        const msg = resp?.data?.error || "No se pudo completar la operación.";
        Swal.fire("Error", msg, "error");
        return;
      }

      const { moved = [], already_present = [], errors = [] } = resp.data || {};

      const li = (txt) => `<li class="fs-6 list-unstyled">${_.escape(txt)}</li>`;
      const list = arr =>
        arr.length
          ? `<ul>${arr
            .map(it => li(it.full_name || ("#" + (it.source_postulation_id || it.id))))
            .join("")}</ul>`
          : "";

      const html = `
        <p><strong>Procesados correctamente:</strong> ${moved.length}</p>
        ${list(moved)}
        <p><strong>Ya estaban en el proceso destino:</strong> ${already_present.length}</p>
        ${list(already_present)}
        <p><strong>Con errores:</strong> ${errors.length}</p>
        ${
        errors.length
          ? `<ul>${errors
            .map(er => `<li class="fs-6 list-unstyled"><strong>${_.escape(er.full_name || ("#" + er.id))}:</strong> ${_.escape(er.error || "Error")}</li>`)
            .join("")}</ul>`
          : ""
      }
      `;

      const icon = errors.length ? "error" : (already_present.length ? "info" : "success");

      Swal.fire({
        title: courseId ? "Resultado del movimiento" : "Resultado de la asignación",
        html,
        icon,
        confirmButtonText: "Aceptar",
      }).then(() => {
        toggle();
        reloadQuery?.();
      });
    });
  };

  return (
    <Fragment>
      <Button color="secondary" outline onClick={toggle}>
        {courseId ? "Mover a otro proceso" : "Asignar a proceso"}
      </Button>

      <Modal isOpen={modal} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          {courseId ? "Mover postulantes a otro proceso" : "Asignar postulantes a un proceso"}
        </ModalHeader>

        <ModalBody>
          <div className="mb-3">
            <Label className="mb-1">Proceso destino</Label>
            <FieldWrapper errors={errors.target ? [errors.target] : []}>
              <Select
                placeholder="Seleccione proceso destino"
                options={courses}
                value={targetCourse}
                onChange={setTargetCourse}
                isClearable
                getOptionValue={(o) => str(o.value)}
                getOptionLabel={(o) => o.label}
              />
            </FieldWrapper>
          </div>

          <div className="mb-1">
            <Label className="mb-1">Postulantes</Label>
            <FieldWrapper errors={errors.applicants ? [errors.applicants] : []}>
              <Select
                isMulti
                placeholder="Seleccione postulantes"
                options={availableOptions}
                value={selectedApplicants}
                onChange={(vals) => {
                  const norm = (vals || []).map(v => ({
                    value: str(v.value),
                    label: v.label,
                    kind:  courseId ? (v.kind || "applicant") : "applicant"
                  }));
                  const uniq = _.uniqBy(norm, (x) => `${x.kind}:${x.value}`);
                  setSelectedApplicants(uniq);
                }}
                hideSelectedOptions
                closeMenuOnSelect={false}
                noOptionsMessage={() => "No hay más postulantes disponibles"}
                getOptionValue={(o) => str(o.value)}
                getOptionLabel={(o) => o.label}
                isOptionSelected={(o) => {
                  const byId = selectedApplicants.some(s => str(s.value) === str(o.value));
                  if (byId) return true;
                  return selectedApplicants.some(s => normTxt(s.label) === normTxt(o.label));
                }}
                menuPlacement="auto"
              />
            </FieldWrapper>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color="success" onClick={onSubmit}>{courseId ? "Mover" : "Asignar"}</Button>
          <Button color="danger" onClick={toggle}>Cancelar</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default MoveToCourseModal;
